import type { IDayObject, IFullCalendar, IUseCalendar, IView } from './types';

import snowmentV2 from '@snowit/utils/snowmentv2';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const DAYS_ROWS = 6;
const DAYS_COLUMNS = 7;
const DAYS_IN_TABLE = DAYS_COLUMNS * DAYS_ROWS; // 42 - 7 columns and 6 rows

export default (view: string, options: IUseCalendar = {}): IFullCalendar => {
  const double: boolean = Boolean(options.double || false);

  const calcDay = (day: number, ref: string, inView: boolean): IDayObject => {
    const refObject = snowmentV2(ref);
    const month = refObject.month;
    const year = refObject.year;
    const date = snowmentV2(
      `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    );
    const dow = date.weekday;
    return {
      value: date.format(),
      number: day,
      in_view: inView,
      break_left: dow === 1 || day === 1,
      break_right: dow === 7 || day === date.days_in_month,
      today: date.isSame(snowmentV2(), 'day'),
    };
  };

  const calcWeek = (date: string): IView => {
    const [startWeek, endWeek] = snowmentV2(date).getStartAndEndOfWeek();
    const dates = snowmentV2.enumerateDays(startWeek, endWeek);
    const start = snowmentV2(startWeek);
    const month = start.month - 1;
    const year = start.year;

    return {
      month: MONTHS[month],
      year: year,
      label: [startWeek, endWeek].map((dt) => snowmentV2(dt).format('very_short')).join(' - '),
      days: dates.map((entry) => {
        const number = snowmentV2(entry).day;
        return calcDay(number, entry, true);
      }),
    };
  };

  const calcView = (date: string, onlyThisMonth: boolean = false): IView => {
    const dateObject = snowmentV2(date);
    const currentMonth = dateObject.startOf('month');
    const previousMonth = dateObject.date.minus({ months: 1 });
    const nextMonth = dateObject.date.plus({ months: 1 });

    // dates
    const dts = {
      prev: previousMonth.toFormat('yyyy-MM-dd'),
      current: currentMonth.toFormat('yyyy-MM-dd'),
      next: nextMonth.toFormat('yyyy-MM-dd'),
    };
    // days in month
    const dim = {
      prev: previousMonth.daysInMonth ?? 0,
      current: currentMonth.daysInMonth ?? 0,
      next: nextMonth.daysInMonth ?? 0,
    };

    const first = currentMonth.weekday;

    const diffs = {
      prev: (first + 6) % 7,
      next: DAYS_IN_TABLE - (dim.current + ((first + 6) % 7)),
    };

    const month = dateObject.month - 1;
    const year = dateObject.year;

    const previous = new Array(diffs.prev)
      .fill(0)
      .map((_, i) => calcDay(dim.prev - i, dts.prev, !double && !onlyThisMonth))
      // .map((it) => onlyThisMonth ? null : it)
      .reverse();

    const current = new Array(dim.current).fill(0).map((_, i) => calcDay(i + 1, dts.current, true));

    const next = new Array(diffs.next)
      .fill(0)
      .map((_, i) => calcDay(i + 1, dts.next, !double && !onlyThisMonth));

    return {
      month: MONTHS[month],
      year: year,
      days: [...previous, ...current, ...next],
    };
  };

  const calcFullyear = (num: number = 12) => {
    const from = snowmentV2(options.start);
    const ret: IView[] = [];
    const year = from.year;
    const month = from.month;
    for (let i = 0; i < num; i++) {
      const date = snowmentV2(`${year}-${String(month).padStart(2, '0')}-01`)
        .add(i, 'months')
        .format();
      const _view = calcView(date, true);
      ret.push(_view);
    }
    return ret;
  };

  const dateNext = snowmentV2(view).add(1, 'months').format();

  if (options.weekly) {
    return {
      current: calcWeek(view),
      next: undefined,
      full_months: [],
      months: MONTHS,
      weekdays: WEEKDAYS,
    };
  }

  return {
    current: calcView(view),
    next: double ? calcView(dateNext) : undefined,
    full_months: calcFullyear(options.months),
    months: MONTHS,
    weekdays: WEEKDAYS,
  };
};
